import { type FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { useCellCoordinate } from '@/components/UI/DataTableV2/hooks/useCellCoordinate';
import { useCellData } from '@/components/UI/DataTableV2/hooks/useCellData';
import { useCellState } from '@/components/UI/DataTableV2/hooks/useCellState';
import { useCellSelectionState } from '@/components/UI/DataTableV2/hooks/useCellSelectionState';
import { useCellAlerts } from '@/components/UI/DataTableV2/hooks/useCellAlerts';
import { useCellMouseHandlers } from '@/components/UI/DataTableV2/hooks/useCellMouseHandlers';
import { useCellPositioning } from '@/components/UI/DataTableV2/hooks/useCellPositioning';
import cn from 'classnames';
import styles from '@/components/UI/DataTableV2/DataTableV2.module.scss';
import { type Nullable } from '@/model/Common.model';
import { getSampleStatusDisplay, type SampleStatus } from '@/model/Sample.model';

type SampleStatusCellProps = {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
};

const isSampleStatus = (value: Nullable<string>): value is SampleStatus =>
  value === 'planned' || value === 'collected' || value === 'cancelled';

export const SampleStatusCell: FC<SampleStatusCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  // Foundation
  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);

  // Data & State
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { hasAlert } = useCellAlerts(idCoordinate, cellRef);

  // Interaction
  const { handleMouseDown, handleMouseEnter, handleMouseUp } = useCellMouseHandlers(coordinate);

  // Styling
  const { containerStyle } = useCellPositioning(positionOffset);
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseUp={handleMouseUp}
    >
      {cellState === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <div
          className={cn(styles.Cell, styles[selectionState], 'truncate', styles[cellState], styles.ReadonlyColumn)}
          data-celltype="sample-status"
          data-state={cellState}
        >
          {isSampleStatus(cellData?.value) ? (
            <span className={cn(styles.Chip, styles[cellData.value])}>{getSampleStatusDisplay(cellData.value)}</span>
          ) : null}
        </div>
      )}
      {hasAlert && <div className={styles.CellAlert} />}
    </div>
  );
};
